import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Box, CircularProgress } from "@mui/material";

import { decryptAesZeroPadding } from "../utils/crypto";
import { GetProfile } from "../redux/actions/profileActions";
import { GetAddress, GetCompanyAction } from "../redux/actions/companyAction";
import { useDispatch, useSelector } from "react-redux";
import outWardService from "../services/outWardService";
import useAlert from "../hooks/useAlert";

const injectCheckoutHtml = (container, html) => {
    if (!container || !html) return;

    container.innerHTML = html;
    container.querySelectorAll("script").forEach((oldScript) => {
        const newScript = document.createElement("script");
        Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
    });
};

export const PaymentPage = () => {
    const location = useLocation();
    const checkoutRef = useRef(null);
    const paymentStartedRef = useRef(false);

    const [profileLoading, setProfileLoading] = useState(true);
    const [checkoutReady, setCheckoutReady] = useState(false);
    const [pageError, setPageError] = useState(null);

    const [user, setUser] = useState(null);
    const [company, setCompany] = useState(null);
    const [adres, setAdres] = useState(null);
    const [paymentResponse, setPaymentResponse] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(null);

    const dispatch = useDispatch();
    const { showSuccessAlertPayment } = useAlert();

    const encryptedBase64 = location.pathname.replace(/^\/payment\/?/, "");
    const authUserId = useSelector((state) => state?.auth?.user?.id);
    const token = useSelector((state) => state?.auth?.user?.accessToken?.token);

    let decryptedText = "";
    let decryptFailed = false;
    try {
        decryptedText = decryptAesZeroPadding(encryptedBase64);
    } catch (error) {
        console.error("Decrypt sırasında hata oluştu:", error);
        decryptFailed = true;
    }

    useEffect(() => {
        if (!authUserId || !token) return;

        const getAddress = async (companyId) => {
            const response = await dispatch(GetAddress(companyId, token));
            setAdres(response?.data[0]);
            return response;
        };

        const getCompany = async () => {
            const response = await dispatch(GetCompanyAction(authUserId, token));
            setCompany(response?.data[0]);
            if (response?.data[0]?.id) {
                await getAddress(response.data[0].id);
            }
            return response;
        };

        const getProfileDetail = async () => {
            try {
                setProfileLoading(true);
                setPageError(null);
                const response = await dispatch(GetProfile(authUserId, token));
                setUser(response);
                await getCompany();
            } catch (error) {
                console.error("Error fetching profile:", error);
                setPageError("Profil bilgileri yüklenemedi. Lütfen tekrar deneyin.");
            } finally {
                setProfileLoading(false);
            }
        };

        getProfileDetail();
    }, [authUserId, token, dispatch]);

    useEffect(() => {
        if (decryptFailed || !decryptedText) {
            setPageError("Ödeme bağlantısı geçersiz veya süresi dolmuş.");
            return;
        }

        if (!company || !user || !adres || paymentStartedRef.current) return;

        paymentStartedRef.current = true;

        const startPayment = async () => {
            try {
                const parsedData = JSON.parse(decryptedText);
                const requestModel = {
                    user,
                    company,
                    companyAddresses: adres,
                    Price: parsedData?.Ucret || null,
                    PackageName: parsedData?.Baslik || null,
                    PackageId: parsedData?.ID ? parsedData.ID.toString() : null,
                };
                const result = await outWardService.payment(requestModel);
                if (!result?.data?.content) {
                    setPageError("Ödeme formu oluşturulamadı. Lütfen tekrar deneyin.");
                    return;
                }
                setPaymentResponse(result.data);
            } catch (error) {
                console.error("Error in payment process:", error);
                setPageError("Ödeme başlatılırken bir hata oluştu.");
            }
        };

        startPayment();
    }, [company, user, adres, decryptedText, decryptFailed]);

    useEffect(() => {
        if (!paymentResponse?.content || !checkoutRef.current) return;

        injectCheckoutHtml(checkoutRef.current, paymentResponse.content);
        setCheckoutReady(true);
    }, [paymentResponse]);

    useEffect(() => {
        if (!paymentResponse?.paymentId || paymentStatus?.status === true) return;

        let intervalId;

        const fetchPaymentStatus = async () => {
            try {
                const status = await outWardService.paymentStatus("Iyzico", paymentResponse.paymentId);
                setPaymentStatus(status);
                if (status?.success || status?.status === true) {
                    clearInterval(intervalId);
                }
            } catch (error) {
                console.error("Error fetching payment status:", error);
            }
        };

        intervalId = setInterval(fetchPaymentStatus, 5000);
        fetchPaymentStatus();

        return () => clearInterval(intervalId);
    }, [paymentResponse?.paymentId]);

    useEffect(() => {
        if (paymentStatus?.status === true) {
            showSuccessAlertPayment();
        }
    }, [paymentStatus?.status, showSuccessAlertPayment]);

    const showProfileError =
        !profileLoading && (company == null || adres == null) && !checkoutReady;

    return (
        <Box
            sx={{
                minHeight: "100vh",
                width: "100%",
                boxSizing: "border-box",
                bgcolor: "#f5f5f5",
                px: { xs: 1, sm: 2 },
                py: 2,
                overflowX: "hidden",
                "& #iyzipay-checkout-form": {
                    width: "100%",
                    minHeight: 400,
                },
                "& #iyzipay-checkout-form iframe": {
                    width: "100% !important",
                    maxWidth: "100% !important",
                    minWidth: "100% !important",
                },
            }}
        >
            {pageError && (
                <Alert sx={{ mb: 2 }} variant="outlined" severity="error">
                    {pageError}
                </Alert>
            )}

            {showProfileError && (
                <Alert sx={{ mb: 2 }} variant="outlined" severity="error">
                    Profil bilgilerinizi kontrol ediniz; adres ve şirket alanları eksiksiz olmalıdır.
                </Alert>
            )}

            {profileLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!profileLoading && !pageError && !checkoutReady && !showProfileError && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            <Box
                ref={checkoutRef}
                sx={{
                    width: "100%",
                    display: checkoutReady ? "block" : "none",
                }}
            />
        </Box>
    );
};

export default PaymentPage;
